"use client";

import { useVirtualizer } from "@tanstack/react-virtual";
import { Layers } from "lucide-react";
import { useEffect, useState } from "react";
import { COLUMN_VALUE_FORMAT_OBJ, PROCESSING_MODE_OBJ } from "../domain";
import { applyFilterClauseToRow } from "../app";
import { BULK_EDIT_SUMMARY_KIND_OBJ, formatForEdit, getRowValue, parseFromEdit } from "../app";


import { getHeaderText, VIRTUALIZATION_THRESHOLD } from "./smart-table-shared";

import { renderInlineCell } from "./inline-cell-editor";
import { useSmartTableModelContext } from "./smart-table-model-context";

export function SmartTableBody<TData>() {
  const m = useSmartTableModelContext<TData>();
  const { columnMeta, lists, onRowClick, onInlineEdit, isRowEditableRow, hoveredFilter, setQuickFilter, t, rowMouse, actionColumnIds, table, visibleCols, Select, locale, bulkEditMode, selectedRowIds, setSelectedRowIds, bulkEdits, isBulkSaving, inlineEditCell, setInlineEditCell, commonRowValues, setCommonRowValues, getRowId, editableColumns, commonRowSummary, toggleRowSelection, stickyIds } = m;
  const { virtualizeRows, virtualRowHeight, virtualOverscan, scrollContainerRef, applyStickyTransform, page, pageSize, filters, sort } = m;
  const allRows = table.getFilteredRowModel().rows;
  const shouldVirtualize = virtualizeRows && allRows.length > VIRTUALIZATION_THRESHOLD;
  const virtualizer = useVirtualizer({
    count: shouldVirtualize ? allRows.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => virtualRowHeight,
    overscan: virtualOverscan,
  });
  // Ссылка на контейнер прокрутки появляется после монтирования дочерних компонентов:
  // одна перерисовка после монтирования даёт виртуализатору готовый элемент.
  const [, setMountTick] = useState(0);
  useEffect(() => {
    if (shouldVirtualize) setMountTick(1);
  }, [shouldVirtualize]);
  const virtualItems = shouldVirtualize ? virtualizer.getVirtualItems() : [];
  // Пока размеры контейнера неизвестны (первый кадр, скрытая вкладка), выводим начало списка.
  const usingFallback = shouldVirtualize && virtualItems.length === 0;
  const fallbackCount = Math.min(allRows.length, virtualOverscan * 2 + 10);
  const firstVirtualIndex = virtualItems[0]?.index ?? -1;
  const lastVirtualIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;
  const topPadding = shouldVirtualize ? (virtualItems[0]?.start ?? 0) : 0;
  const bottomPadding = usingFallback
    ? (allRows.length - fallbackCount) * virtualRowHeight
    : shouldVirtualize
      ? virtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)
      : 0;
  const renderedRows = usingFallback
    ? allRows.slice(0, fallbackCount).map((row, rowIndex) => ({ row, rowIndex }))
    : shouldVirtualize
    ? virtualItems.flatMap((item) => {
        const row = allRows[item.index];
        return row ? [{ row, rowIndex: item.index }] : [];
      })
    : allRows.map((row, rowIndex) => ({ row, rowIndex }));
  const spacerColSpan = visibleCols.length + (bulkEditMode ? 1 : 0);

  // Закреплённые колонки смещаются скриптом: после смены окна строк применяем смещение заново.
  useEffect(() => {
    if (shouldVirtualize) applyStickyTransform();
  }, [shouldVirtualize, firstVirtualIndex, lastVirtualIndex, applyStickyTransform]);

  // Смена страницы, фильтров или сортировки возвращает прокрутку к началу.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (shouldVirtualize && container) container.scrollTop = 0;
  }, [shouldVirtualize, page, pageSize, filters, sort, scrollContainerRef]);

  return (
            <tbody>
              {bulkEditMode && (
                <tr
                  className="bulk-apply-row border-b-2 border-primary/30 bg-primary/5"
                  aria-label={t("table.bulkEdit.applyAll")}
                >
                  <td className="w-10 px-2 text-center align-middle">
                    <Layers
                      className="h-4 w-4 text-primary"
                      aria-label={t("table.bulkEdit.applyAll")}
                    />
                  </td>
                  {visibleCols.map((col) => {
                    const columnId = col.id;
                    const isActionCol = actionColumnIds.includes(columnId);
                    const colDefMeta = col.columnDef.meta;
                    const isEditable = editableColumns.includes(columnId);
                    const editList = lists.find(
                      (l) => l.propertyKey === columnId,
                    );
                    const stickyIdx = stickyIds.indexOf(columnId);
                    const isSticky = stickyIdx !== -1;
                    let stickyLeft = 0;
                    if (isSticky) {
                      for (const c of visibleCols) {
                        if (c === col) break;
                        if (stickyIds.includes(c.id)) {
                          stickyLeft += c.getSize();
                        }
                      }
                    }
                    const colMeta = columnMeta[columnId];
                    const cellBg = isSticky
                      ? "var(--background, #fff)"
                      : undefined;
                    const editFmt = colDefMeta?.format;
                    const editFmtOpts = colDefMeta?.formatOptions;
                    const editPrefix =
                      editFmt === COLUMN_VALUE_FORMAT_OBJ.CURRENCY
                        ? (editFmtOpts?.prefix ?? "$")
                        : editFmtOpts?.prefix;
                    const editSuffix =
                      editFmt === COLUMN_VALUE_FORMAT_OBJ.PERCENT
                        ? "%"
                        : editFmtOpts?.suffix;
                    const summary = commonRowSummary[columnId];
                    const userTyped = commonRowValues[columnId];
                    const colHeader =
                      getHeaderText(col.columnDef.header) ?? columnId;
                    let displayText: string;
                    let isMixed = false;
                    if (userTyped !== undefined) {
                      displayText = formatForEdit(
                        userTyped,
                        editFmt,
                        editFmtOpts,
                        locale,
                      );
                    } else if (
                      summary?.kind === BULK_EDIT_SUMMARY_KIND_OBJ.SINGLE
                    ) {
                      displayText = formatForEdit(
                        summary.value,
                        editFmt,
                        editFmtOpts,
                        locale,
                      );
                    } else if (
                      summary?.kind === BULK_EDIT_SUMMARY_KIND_OBJ.MIXED
                    ) {
                      displayText = "";
                      isMixed = true;
                    } else {
                      displayText = "";
                    }
                    const inputDisabled = isBulkSaving;
                    return (
                      <td
                        key={`bulk-apply-${columnId}`}
                        className="p-4 align-middle overflow-hidden whitespace-nowrap"
                        data-sticky-col={isSticky ? "" : undefined}
                        data-sticky-left={isSticky ? stickyLeft : undefined}
                        style={{
                          width: col.getSize(),
                          ...(cellBg ? { backgroundColor: cellBg } : {}),
                          ...(colMeta?.cellTextColor
                            ? { color: colMeta.cellTextColor }
                            : {}),
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isActionCol || !isEditable ? (
                          <span className="text-xs text-text-secondary">
                            —
                          </span>
                        ) : editList ? (
                          <Select
                            size="sm"
                            value={
                              userTyped !== undefined
                                ? String(userTyped)
                                : summary?.kind ===
                                    BULK_EDIT_SUMMARY_KIND_OBJ.SINGLE
                                  ? String(summary.value)
                                  : ""
                            }
                            {...(isMixed
                              ? { placeholder: t("table.bulkEdit.mixed") }
                              : {})}
                            onChange={(v) =>
                              setCommonRowValues((prev) => ({
                                ...prev,
                                [columnId]: v,
                              }))
                            }
                            ariaLabel={t("table.bulkEdit.applyAll")}
                            options={editList.items.map((i) => ({
                              value: i.value,
                              label: i.label[locale] ?? i.value,
                            }))}
                            disabled={inputDisabled}
                          />
                        ) : (
                          <span className="inline-flex items-center gap-0.5">
                            {editPrefix && (
                              <span className="text-text-secondary text-xs">
                                {editPrefix}
                              </span>
                            )}
                            <input
                              type="text"
                              value={displayText}
                              {...(isMixed
                                ? { placeholder: t("table.bulkEdit.mixed") }
                                : {})}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setCommonRowValues((prev) => ({
                                  ...prev,
                                  [columnId]: parseFromEdit(raw, editFmt),
                                }));
                              }}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={t("table.bulkEdit.aria.editField", {
                                column: colHeader,
                              })}
                              disabled={inputDisabled}
                            />
                            {editSuffix && (
                              <span className="text-text-secondary text-xs">
                                {editSuffix}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              )}
              {topPadding > 0 && (
                <tr aria-hidden="true" style={{ height: topPadding }}>
                  <td colSpan={spacerColSpan} style={{ padding: 0 }} />
                </tr>
              )}
              {renderedRows.map(({ row, rowIndex }) => {
                const rowId = getRowId(row.original);
                const rowIsEditable =
                  isRowEditableRow?.(row.original) ?? true;
                const isSelected = selectedRowIds.has(rowId);
                const rowEdit = bulkEdits.get(rowId);
                const matchesHovered =
                  hoveredFilter !== null &&
                  applyFilterClauseToRow(row.original, hoveredFilter);
                return (
                  <tr
                    key={row.id}
                    data-index={rowIndex}
                    style={shouldVirtualize ? { height: virtualRowHeight } : undefined}
                    className={`border-b transition-colors hover:bg-surface/50 cursor-pointer${
                      rowIndex % 2 === 1
                        ? " [background-color:var(--table-zebra,var(--ui-color-surface))]"
                        : ""
                    }${matchesHovered ? " [background-color:var(--table-row-highlight,var(--ui-color-warning-soft))]" : ""}`}
                    onMouseDown={(e) => {
                      if (bulkEditMode) return;
                      rowMouse.current = {
                        rowId: row.id,
                        startX: e.clientX,
                        startY: e.clientY,
                        moved: false,
                      };
                    }}
                    onMouseUp={(e) => {
                      if (bulkEditMode) return;
                      if (
                        !rowMouse.current ||
                        rowMouse.current.rowId !== row.id
                      )
                        return;
                      const dx = Math.abs(e.clientX - rowMouse.current.startX);
                      const dy = Math.abs(e.clientY - rowMouse.current.startY);
                      if (dx > 3 || dy > 3) {
                        rowMouse.current.moved = true;
                      }
                    }}
                    onClick={() => {
                      if (bulkEditMode) return;
                      const sel = window.getSelection();
                      if (sel && sel.toString().length > 0) return;
                      if (
                        rowMouse.current?.rowId === row.id &&
                        rowMouse.current.moved
                      )
                        return;
                      onRowClick?.(row.original);
                    }}
                    tabIndex={!bulkEditMode && onRowClick ? 0 : -1}
                    onKeyDown={(e) => {
                      if (bulkEditMode) return;
                      if (onRowClick && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onRowClick(row.original);
                      }
                    }}
                  >
                    {bulkEditMode && (
                      <td
                        className="w-10 px-2 text-center align-middle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRowSelection(rowId)}
                          aria-label={t("table.bulkEdit.aria.rowSelect")}
                          disabled={isBulkSaving}
                        />
                      </td>
                    )}
                    {row.getVisibleCells().map((cell) => {
                      const columnId = cell.column.id;
                      const isActionCol = actionColumnIds.includes(columnId);
                      const colMeta = columnMeta[columnId];
                      const stickyIdx = stickyIds.indexOf(columnId);
                      const isSticky = stickyIdx !== -1;
                      const cellBg = colMeta?.cellColor
                        ? rowIndex % 2 === 1
                          ? `color-mix(in srgb, ${colMeta.cellColor} 82%, var(--table-zebra, var(--ui-color-surface)))`
                          : colMeta.cellColor
                        : isSticky
                          ? "var(--background, #fff)"
                          : undefined;
                      let stickyLeft = 0;
                      if (isSticky) {
                        const visibleCols = row.getVisibleCells();
                        for (const c of visibleCols) {
                          if (c === cell) break;
                          if (stickyIds.includes(c.column.id)) {
                            stickyLeft += c.column.getSize();
                          }
                        }
                      }
                      const canInlineEdit =
                        bulkEditMode && editableColumns.includes(columnId);
                      const isInlineEditing =
                        rowIsEditable &&
                        inlineEditCell?.rowId === rowId &&
                        inlineEditCell?.columnId === columnId;
                      const isInlineEditable =
                        rowIsEditable &&
                        !isActionCol &&
                        editableColumns.includes(columnId) &&
                        (!!onInlineEdit || bulkEditMode);
                      const editValue =
                        getRowValue(rowEdit, columnId) ??
                        (isSelected ? commonRowValues[columnId] : undefined);
                      const colHeader =
                        getHeaderText(cell.column.columnDef.header) ?? columnId;
                      return (
                        <td
                          key={cell.id}
                          className={`p-4 align-middle overflow-hidden whitespace-nowrap ${
                            !isActionCol &&
                            !canInlineEdit &&
                            !isInlineEditable &&
                            !isInlineEditing
                              ? "text-text-secondary"
                              : ""
                          }`}
                          data-sticky-col={isSticky ? "" : undefined}
                          data-sticky-left={isSticky ? stickyLeft : undefined}
                          style={{
                            width: cell.column.getSize(),
                            ...(cellBg ? { backgroundColor: cellBg } : {}),
                            ...(colMeta?.cellTextColor
                              ? { color: colMeta.cellTextColor }
                              : {}),
                          }}
                          onClick={(e) => {
                            if (isActionCol || isInlineEditable)
                              e.stopPropagation();
                            if (canInlineEdit && !selectedRowIds.has(rowId)) {
                              setSelectedRowIds((prev) => {
                                const next = new Set(prev);
                                next.add(rowId);
                                return next;
                              });
                            }
                          }}
                          onContextMenu={(e) => {
                            if (isActionCol) return;
                            const meta = cell.column.columnDef.meta;
                            if (meta?.filterable === PROCESSING_MODE_OBJ.NONE)
                              return;
                            e.preventDefault();
                            const cellValue = getRowValue(
                              row.original,
                              columnId,
                            );
                            setQuickFilter({
                              column: columnId,
                              value: cellValue,
                              x: e.clientX,
                              y: e.clientY,
                            });
                          }}
                          onDoubleClick={(e) => {
                            if (isInlineEditable) {
                              e.stopPropagation();
                              const dblMeta = cell.column.columnDef.meta;
                              const currentValue = formatForEdit(
                                cell.getValue(),
                                dblMeta?.format,
                                dblMeta?.formatOptions,
                                locale,
                              );
                              setInlineEditCell({
                                rowId,
                                columnId,
                                value: currentValue,
                              });
                            }
                          }}
                          title={
                            isInlineEditable
                              ? t("table.inlineEdit.doubleClick")
                              : undefined
                          }
                        >
                          {renderInlineCell({ m, cell, columnId, rowId, editValue, isInlineEditing, canInlineEdit, colHeader })}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {bottomPadding > 0 && (
                <tr aria-hidden="true" style={{ height: bottomPadding }}>
                  <td colSpan={spacerColSpan} style={{ padding: 0 }} />
                </tr>
              )}
            </tbody>
  );
}
